import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { useApplicationProvider, ApplicationProvider, useApplicationChanged, useLocationStepProvider } from '@/applications/application-provider';
import { HomeOutlined } from '@ant-design/icons';
import { Location, CareType, CarePackage } from '@/client/models';
import { InfoCard } from '@/components/info-card';
import { LoadingContainer } from '@/components/loading-container';
import { locationsRetrievedSelector } from '@/store/application/selectors';
import { Status } from '@/store/status';
import { GridLayout, GridLayoutItem } from '@/components/grid-layout';
import { useTranslate } from '@/language-provider';
import { Divider, Select } from 'antd';
import { FormItem } from '../components/form-item';

const LocationComponent: React.FC<{ location: Location, isLast: boolean }> = ({ location, isLast }) => {
    const applicationProvider = useApplicationProvider();
    const provider = useLocationStepProvider();
    const [showPackages, setShowPackages] = useState(provider.isLocationSelected(location.locationId));
    const [showMunic, setShowMunic] = useState(provider.hasSubsidyPlace());
    const [hasEmptyMunic, setHasEmptyMunic] = useState(typeof provider.getSelectedMunicipality() === 'undefined');
    const { translate } = useTranslate();

    function addressLine1(location: Location): string {
        const a = location.address;
        return `${a.street} ${a.number ?? ''}${a.extension ?? ''}`;
    }
    function addressLine2(location: Location): string {
        const a = location.address;
        return `${a.zipCode} ${a.city}`;
    }

    const columns = 1;
    const rows = location.carePackages.length / columns;
    const municipalities = provider.getMunicipalities();
    const isLastLoc = isLast;

    useApplicationChanged(() => {
        const hasSubsidyPlace = provider.hasSubsidyPlace();
        setShowMunic(hasSubsidyPlace);
        if (!hasSubsidyPlace) {
            const currentMunic = provider.getSelectedMunicipality();
            if (currentMunic) {
                provider.setMunicipality(undefined);
            }
        } else {
            if (municipalities.length === 1) {
                provider.setMunicipality(municipalities[0].municipalityId);
            }
            else {
                const currentMunic = provider.getSelectedMunicipality();
                setHasEmptyMunic(typeof currentMunic === 'undefined');
            }
        }
    });

    const getDefaultMunicipality = (): string => {
        const m = provider.getSelectedMunicipality();
        if (!m) {
            return undefined;
        }
        return m.municipalityId;
    };
    

    return (
        <div>
            <InfoCard
                icon={<HomeOutlined />}
                imageUrl={location.imageUrl}
                title={location.name}
                lines={[addressLine1(location), addressLine2(location)]}
                onClick={() => setShowPackages(!showPackages)} />

            {showPackages &&
                <div style={{paddingLeft: '32px'}}>
                    <GridLayout columns={columns} rows={rows} rowgap="8px" colgap="8px">
                        {location.carePackages.map((x, i) => (
                            <GridLayoutItem key={x.carePackageId} column={i / rows + 1} row={i % rows + 1}>
                                <CarePackageComponent location={location} carePackage={x} applicationProvider={applicationProvider} />
                            </GridLayoutItem>
                        ))}
                    </GridLayout>
                    <Divider />
                </div>
            }
            {municipalities.length > 1 && location.carePackages.length > 0 && isLastLoc &&
                    showMunic &&
                <FormItem name='municipality' required={true} style={{width: '450px', padding: '8px'}} >
                    <Select onChange={provider.setMunicipality} defaultValue={getDefaultMunicipality()}>
                        {municipalities.map(x => <Select.Option key={x.municipalityId} value={x.municipalityId}>{x.name}</Select.Option>)}
                    </Select>
                    <div style={{color: 'rgba(0, 0, 0, 0.45)'}}>{translate('applications.data.municipalityExplain')}</div>
                    {hasEmptyMunic &&
                        <div style={{color: '#ff4d4f'}}>
                            {translate('global.generic.requiredFormat', { label: translate('applications.data.municipality') }).toString()}
                        </div>
                    }
                </FormItem>
            }
            
        </div>
    );
};

const CarePackageComponent: React.FC<{ location: Location; carePackage: CarePackage; applicationProvider: ApplicationProvider }> = ({ location, carePackage, applicationProvider }) => {
    const { translate } = useTranslate();
    const provider = applicationProvider.getLocationStepProvider();
    const [isSelected, setIsSelected] = useState(provider.isLocationPackageSelected(location.locationId, carePackage.carePackageId));

    applicationProvider.onChanged(() => setIsSelected(provider.isLocationPackageSelected(location.locationId, carePackage.carePackageId)));

    function line1(): string {
        return `${translate(`global.careType.${carePackage.careType}`)} ${carePackage.numberOfWeeks} ` + translate('global.weeks');
    }

    function code(): string {
        if (carePackage.careType === CareType.Daycare) {
            return 'KDV';
        } else if (carePackage.careType === CareType.Playschool) {
            return 'POP';
        } else if (carePackage.careType === CareType.BetweenSchoolCare) {
            return 'TSO';
        } else if (carePackage.careType === CareType.HolidayCare) {
            return 'VAK';
        }
        return 'BSO';
    }
    return (
        <InfoCard code={code()} title={carePackage.name} lines={[line1()]} isSelected={isSelected} onClick={() => provider.toggleLocationPackage(location, carePackage)} />
    );
};

export const LocationsStep: React.FC = () => {
    const retrieved = useSelector(locationsRetrievedSelector);

    if (retrieved.status !== Status.Loaded) {
        return <LoadingContainer />;
    }

    return (<>{retrieved.value.map((x, i, rows) => <LocationComponent key={x.locationId} location={x} isLast={i+1 === rows.length}/>)}</>);
};